import { ServiceResult } from './ServiceEntities';
import { Post, GetManagementServiceRootUrlWithoutState } from './ServiceManager';
import { GbBookingInfo, GbBulkBookingStatusUpdateResponse, GbBulkBookingStatusUpdateRequest, GbBookingVehicleLocation, GbDriverPhoto } from "./GbBookingEntities";
import { MyBookingsRequest, BookingAuthority } from './BookingEntities';

/** Base URL for the GbBookingController in the Booking Management Service. */
function GetBaseUrl(): string {
    return `${GetManagementServiceRootUrlWithoutState()}GbBooking/`;
}

/** Client for the BookingController in the Management API */
class GbBookingServiceClass {    

    /** Get booking details using booking ID. */
    public GetBookingDetails(bookingKey: BookingAuthority): Promise<ServiceResult<GbBookingInfo>> {
        const url = `${GetBaseUrl()}GetBookingDetails`;
        return Post<GbBookingInfo>(url, bookingKey);
    }

    /** Cancel a booking using booking ID. */
    public CancelBooking(bookingKey: BookingAuthority): Promise<ServiceResult<void>> {
        const url = `${GetBaseUrl()}Cancel`;
        return Post<void>(url, bookingKey);
    }

    /** Lightweight booking status updates for multiple bookings. */
    public GetBulkBookingUpdates(request: GbBulkBookingStatusUpdateRequest): Promise<ServiceResult<GbBulkBookingStatusUpdateResponse>> {
        const url = `${GetBaseUrl()}BookingStatusBulk`;
        return Post<GbBulkBookingStatusUpdateResponse>(url, request);
    }

    /** Load user's (currently logged in user) bookings. */
    public GetMyBookings(request: MyBookingsRequest): Promise<ServiceResult<GbBookingInfo[]>> {
        const url = `${GetBaseUrl()}GetMyBookings`;
        return Post<GbBookingInfo[]>(url, request);
    }

    /**
     * Check the validity (only) of a booking link.
     */
    public CheckLink(link: BookingAuthority): Promise<ServiceResult<void>> {
        const url = `${GetBaseUrl()}CheckLink`;
        return Post<void>(url, link);
    }

    /**
     * Load one or more bookings by their links.
     */
    public GetBookingsByLinkBulk(links: BookingAuthority[]): Promise<ServiceResult<GbBookingInfo[]>> {
        const url = `${GetBaseUrl()}GetByLinkBulk`;
        return Post<GbBookingInfo[]>(url, links);
    }

    /**
     * Vehicle locations only, for multiple bookings.
     * Data will only be available for bookings in the Accepted or PickedUp statuses.
     */
    public GetBookingVehicleLocations(links: BookingAuthority[]): Promise<ServiceResult<GbBookingVehicleLocation[]>> {
        const url = `${GetBaseUrl()}BookingVehicleLocations`;
        return Post<GbBookingVehicleLocation[]>(url, links);
    }

    /**
     * Get the driver photo of the driver assigned to a booking.
     */
    public GetBookingDriverPhoto(bookingKey: BookingAuthority): Promise<ServiceResult<GbDriverPhoto>> {
        const url = `${GetBaseUrl()}BookingDriverPhoto`;
        return Post<GbDriverPhoto>(url, bookingKey);
    }
}

/** Singleton export for static access to the operations */
export const GbBookingService = new GbBookingServiceClass();
